import React, { useState } from 'react';
import { X, Plus, AlertCircle, CheckCircle } from 'lucide-react';
import { stcPriorityService, CreateStcPriorityRequest } from '../../../../../services/stcPriorityService';
import { useAuth } from '../../../../../contexts/AuthContext';

interface PriorityCreateModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const PriorityCreateModal: React.FC<PriorityCreateModalProps> = ({
    isOpen,
    onClose,
    onSuccess
}) => {
    const { user } = useAuth();

    const [formData, setFormData] = useState<CreateStcPriorityRequest>({
        companyNo: user?.companyNo || '001',
        priorityNo: 1,
        description1: ''
    });
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.priorityNo || formData.priorityNo < 1) {
            setError('Prioritet nr. er obligatorisk');
            return;
        }
        if (!formData.description1?.trim()) {
            setError('Beskrivelse er obligatorisk');
            return;
        }

        try {
            setIsSaving(true);
            setError('');
            await stcPriorityService.create(formData);

            setShowSuccess(true);
            setTimeout(() => {
                setShowSuccess(false);
                onSuccess();
            }, 1200);
        } catch (err: any) {
            setError(err.message || 'Feil ved opprettelse');
        } finally {
            setIsSaving(false);
        }
    };

    const updateField = <K extends keyof CreateStcPriorityRequest>(key: K, value: CreateStcPriorityRequest[K]) => {
        setFormData(prev => ({ ...prev, [key]: value }));
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
            <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

            <div className="absolute inset-0 flex items-center justify-center p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-green-600 to-emerald-600 rounded-t-xl">
                        <div>
                            <h2 className="text-lg font-semibold text-white">Ny plasseringsprioritet</h2>
                            <p className="text-sm text-green-100">Opprett en ny plasseringsprioritet</p>
                        </div>
                        <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Content */}
                    <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
                        {error && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <div className="flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                    <p className="text-sm text-red-800">{error}</p>
                                </div>
                            </div>
                        )}

                        {showSuccess && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                <div className="flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                    <p className="text-sm text-green-800 font-medium">Plasseringsprioritet opprettet!</p>
                                </div>
                            </div>
                        )}

                        <fieldset className="border border-gray-200 rounded-lg p-4">
                            <legend className="text-sm font-semibold text-gray-700 px-2">Plasseringsprioritet</legend>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Prioritet nr. <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.priorityNo}
                                        onChange={(e) => updateField('priorityNo', parseInt(e.target.value) || 0)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        required
                                        min={1}
                                        autoFocus
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Beskrivelse <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.description1}
                                        onChange={(e) => updateField('description1', e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        required
                                        maxLength={100}
                                        placeholder="F.eks. Høy prioritet, Lav prioritet..."
                                    />
                                </div>
                            </div>
                        </fieldset>

                        {/* Footer */}
                        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                Avbryt
                            </button>
                            <button
                                type="submit"
                                disabled={isSaving}
                                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                    isSaving
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-green-600 text-white hover:bg-green-700'
                                }`}
                            >
                                {isSaving ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        Oppretter...
                                    </>
                                ) : (
                                    <>
                                        <Plus className="w-4 h-4" />
                                        Opprett
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};
